import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import './difot.css';
import {
  categoriseFailure,
  coerceRow,
  computeMetrics,
  DATE_PRESETS,
  difotTone,
  FAIL_CATEGORIES,
  formatBoundsLabel,
  formatDateAU,
  formatTimeAU,
  inBounds,
  latestDate,
  rangeBounds,
  STATUS_COLOURS,
  ZONE_COLOURS,
  ZONE_ORDER,
  type DateBounds,
  type DateRange,
  type Metrics,
  type Period,
  type RawRow,
  type Row,
} from './difot/lib';
import { TrendChart, ZoneChart } from './difot/TrendChart';
import { TVView } from './difot/TVView';

interface Filters {
  period: Period;
  dateRange: DateRange;
  customFrom: string; // YYYY-MM-DD, only used when dateRange === 'custom'
  customTo: string;
  zone: string;
  driver: string;
  status: string;
}

/**
 * Filter rows to a date window. Presets are anchored to the real calendar day
 * in Australia/Melbourne (not the latest day present in the data), so
 * "Yesterday" means yesterday's date — matching what people expect — and the
 * maths is done on YYYY-MM-DD strings, sidestepping timezone drift.
 */
function narrowByRange(rows: Row[], range: DateRange, custom?: { from: string; to: string }): Row[] {
  if (range === 'all') return rows;
  const b = rangeBounds(range, { from: custom?.from, to: custom?.to });
  // A custom range with neither end chosen yet shouldn't blank the dashboard.
  if (range === 'custom' && !b.from && !b.to) return rows;
  return rows.filter((r) => inBounds(r.d, b));
}

export function Difot() {
  const [data, setData] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState('just now');
  const [tv, setTv] = useState(false);
  const [filters, setFilters] = useState<Filters>({ period: 'daily', dateRange: 'all', customFrom: '', customTo: '', zone: '', driver: '', status: '' });

  async function load() {
    const raw = await apiFetch<RawRow[]>('/api/reports/difot');
    setData((Array.isArray(raw) ? raw : []).map(coerceRow));
    setLastSync(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
  }

  useEffect(() => {
    let active = true;
    apiFetch<RawRow[]>('/api/reports/difot')
      .then((raw) => {
        if (!active) return;
        setData((Array.isArray(raw) ? raw : []).map(coerceRow));
        setLastSync(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load DIFOT data');
      });
    const timer = setInterval(() => {
      load().catch(() => undefined);
    }, 60_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = narrowByRange(data, filters.dateRange, { from: filters.customFrom, to: filters.customTo });
    if (filters.zone) rows = rows.filter((r) => r.r === filters.zone);
    if (filters.driver) rows = rows.filter((r) => r.dr === filters.driver);
    if (filters.status) rows = rows.filter((r) => r.st === filters.status);
    return rows;
  }, [data, filters]);

  const dateBounds = useMemo<DateBounds>(
    () => rangeBounds(filters.dateRange, { from: filters.customFrom, to: filters.customTo }),
    [filters.dateRange, filters.customFrom, filters.customTo],
  );

  const metrics = useMemo(() => computeMetrics(filtered), [filtered]);

  const zones = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.map((r) => r.r).filter(Boolean) as string[])].sort(
      (a, b) => ZONE_ORDER.indexOf(a) - ZONE_ORDER.indexOf(b),
    );
  }, [data]);
  const drivers = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.map((r) => r.dr).filter(Boolean) as string[])].sort();
  }, [data]);

  const rangeLabel = useMemo(() => {
    const dates = [...new Set(filtered.map((r) => r.d).filter(Boolean) as string[])].sort();
    if (!dates.length) return 'No data';
    return dates.length === 1 ? formatDateAU(dates[0]) : `${formatDateAU(dates[0])} – ${formatDateAU(dates[dates.length - 1])}`;
  }, [filtered]);

  if (tv && data) {
    return <TVView data={data} onExit={() => setTv(false)} />;
  }

  return (
    <div className="difot-root">
      <div className="difot-wrap">
        <header className="difot-header">
          <div className="d-center d-gap-3">
            <div className="brand-badge">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
                <circle cx="12" cy="9" r="3" />
              </svg>
            </div>
            <div>
              <div className="brand-eyebrow">
                Feramex · <span>Powered by Locate2u</span>
              </div>
              <h1 className="brand-title">DIFOT Performance Dashboard</h1>
            </div>
          </div>
          <div className="d-center d-gap-3">
            <div className="meta-right">
              <div>
                Data window <span className="strong">{rangeLabel}</span> ({filtered.length.toLocaleString()} stops)
              </div>
              <div style={{ marginTop: 2 }}>Updated {lastSync}</div>
            </div>
            <button
              className="btn"
              onClick={() => {
                load().catch(() => undefined);
              }}
            >
              Refresh
            </button>
            <button className="btn btn-primary" onClick={() => setTv(true)} disabled={!data}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="14" rx="2" />
                <path d="M7 22h10" />
                <path d="M12 18v4" />
              </svg>
              TV View
            </button>
          </div>
        </header>

        {error && (
          <div className="error-banner" role="alert">
            <strong>Couldn&rsquo;t load data.</strong> {error}
          </div>
        )}
        {!error && data === null && (
          <div className="boot-banner">
            <span className="spin" />
            Loading delivery data…
          </div>
        )}

        {data && (
          <>
            <FilterBar filters={filters} setFilters={setFilters} zones={zones} drivers={drivers} bounds={dateBounds} count={filtered.length} />
            <KpiStrip m={metrics} className="mb-5" />

            <section className="card card-pad mb-5 rise">
              <div className="d-between mb-5" style={{ marginBottom: 16 }}>
                <div>
                  <div className="sectiontitle">DIFOT Trend</div>
                  <p className="section-sub">Delivered In Full, On Time across the selected period</p>
                </div>
                <div className="chart-legend">
                  <span className="d-center d-gap-2">
                    <span className="dot" style={{ background: '#0BDA8E' }} />
                    DIFOT %
                  </span>
                  <span className="d-center d-gap-2">
                    <span className="dot" style={{ background: '#0061A2' }} />
                    Volume
                  </span>
                </div>
              </div>
              <TrendChart rows={filtered} period={filters.period} />
            </section>

            <div className="two-col mb-5">
              <section className="card card-pad rise">
                <div className="mb-5" style={{ marginBottom: 12 }}>
                  <div className="sectiontitle">Performance by Metro Zone</div>
                  <p className="section-sub">Volume and DIFOT % across delivery zones</p>
                </div>
                <ZoneChart rows={filtered} />
                <ZoneCards rows={filtered} />
              </section>

              <FailureList rows={filtered} />
            </div>

            <OrderSearch data={data} />

            <DriverLeaderboard
              rows={filtered}
              onPickDriver={(name) => setFilters((f) => ({ ...f, driver: name }))}
            />

            <FailedDeepDive data={data} filtered={filtered} />

            <TodayOps data={data} filtered={filtered} />

            <footer style={{ textAlign: 'center', fontSize: 11, color: '#737474', padding: '16px 0' }}>
              Feramex DIFOT Dashboard · Powered by Locate2u · Data source: <code>v_difot_dashboard</code> (stop_reports + stop_notes)
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

// ── Filter bar ──────────────────────────────────────────────────
function FilterBar({
  filters,
  setFilters,
  zones,
  drivers,
  bounds,
  count,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  zones: string[];
  drivers: string[];
  bounds: DateBounds;
  count: number;
}) {
  return (
    <div className="card card-pad mb-5 rise" style={{ padding: 16 }}>
      <div className="date-filter">
        <label className="field-label">Date Range</label>
        <div className="seg seg-wrap" role="group" aria-label="Date range presets">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.key}
              className={filters.dateRange === p.key ? 'active' : ''}
              aria-pressed={filters.dateRange === p.key}
              onClick={() => setFilters((f) => ({ ...f, dateRange: p.key }))}
            >
              {p.label}
            </button>
          ))}
        </div>
        {filters.dateRange === 'custom' && (
          <div className="date-custom">
            <label>
              <span className="field-label">From</span>
              <input
                type="date"
                className="input-base input-sm"
                value={filters.customFrom}
                max={filters.customTo || undefined}
                onChange={(e) => setFilters((f) => ({ ...f, customFrom: e.target.value }))}
              />
            </label>
            <label>
              <span className="field-label">To</span>
              <input
                type="date"
                className="input-base input-sm"
                value={filters.customTo}
                min={filters.customFrom || undefined}
                onChange={(e) => setFilters((f) => ({ ...f, customTo: e.target.value }))}
              />
            </label>
          </div>
        )}
        <div className="date-summary">
          <span className="strong">{formatBoundsLabel(bounds)}</span>
          <span className="muted"> · {count.toLocaleString()} stop{count === 1 ? '' : 's'}</span>
          {filters.dateRange === 'custom' && !bounds.from && !bounds.to && (
            <span className="muted"> · pick a start and/or end date</span>
          )}
        </div>
      </div>

      <div className="filter-grid" style={{ marginTop: 16 }}>
        <div className="col-3">
          <label className="field-label">Period</label>
          <div className="seg">
            {(['daily', 'weekly', 'monthly'] as Period[]).map((p) => (
              <button key={p} className={filters.period === p ? 'active' : ''} onClick={() => setFilters((f) => ({ ...f, period: p }))}>
                {p[0].toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="col-3">
          <label className="field-label">Metro Zone</label>
          <select className="input-base" value={filters.zone} onChange={(e) => setFilters((f) => ({ ...f, zone: e.target.value }))}>
            <option value="">All zones</option>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
        <div className="col-3">
          <label className="field-label">Driver</label>
          <select className="input-base" value={filters.driver} onChange={(e) => setFilters((f) => ({ ...f, driver: e.target.value }))}>
            <option value="">All drivers</option>
            {drivers.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="col-3">
          <label className="field-label">Status</label>
          <select className="input-base" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All statuses</option>
            <option value="Complete">Complete</option>
            <option value="Failed">Failed</option>
            <option value="Pending">Pending</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>
    </div>
  );
}

// ── KPI strip ───────────────────────────────────────────────────
function KpiStrip({ m, className }: { m: Metrics; className?: string }) {
  const cards = [
    { label: 'DIFOT', value: m.difotPct.toFixed(1) + '%', sub: `${m.difot} of ${m.denom}`, color: '#0BDA8E' },
    { label: 'Deliveries', value: m.completed.toLocaleString(), sub: `of ${m.total.toLocaleString()} stops`, color: '#0061A2' },
    { label: 'On Time', value: m.onTimePct.toFixed(1) + '%', sub: 'of completed', color: '#1A85C9' },
    { label: 'Failed', value: m.failed.toLocaleString(), sub: `${m.failurePct.toFixed(1)}% failure rate`, color: '#FF5245' },
    { label: 'Pending', value: m.pending.toLocaleString(), sub: `${m.cancelled} cancelled`, color: '#B37D6C' },
    { label: 'Avg Service', value: m.avgService.toFixed(1) + 'm', sub: `${m.drivers} active drivers`, color: '#003D6C' },
  ];
  return (
    <div className={`kpi-grid ${className ?? ''}`}>
      {cards.map((c) => (
        <div className="kpi-card rise" key={c.label}>
          <div className="accent" style={{ background: c.color }} />
          <div className="kpi-label">{c.label}</div>
          <div className="kpi-value num-mono" style={{ color: c.color }}>
            {c.value}
          </div>
          <div className="kpi-sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ── Zone summary cards ──────────────────────────────────────────
function ZoneCards({ rows }: { rows: Row[] }) {
  const byZone: Record<string, { total: number; difot: number; denom: number }> = {};
  rows.forEach((r) => {
    const z = r.r || 'Other';
    if (!byZone[z]) byZone[z] = { total: 0, difot: 0, denom: 0 };
    byZone[z].total++;
    if (r.st !== 'Cancelled') byZone[z].denom++;
    if (r.st === 'Complete' && r.ot === true) byZone[z].difot++;
  });
  const order = Object.keys(byZone).sort((a, b) => byZone[b].total - byZone[a].total);
  return (
    <div className="zone-cards mt-3">
      {order.map((z) => {
        const v = byZone[z];
        const pct = v.denom ? (v.difot / v.denom) * 100 : 0;
        return (
          <div className="zone-card" key={z}>
            <div className="d-between" style={{ marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: ZONE_COLOURS[z] || '#384553' }}>{z}</span>
              <span style={{ fontSize: 10, color: '#737474' }}>{v.total} stops</span>
            </div>
            <div className="num-mono" style={{ fontSize: 16, fontWeight: 600, color: '#003D6C' }}>
              {pct.toFixed(1)}%
            </div>
            <div className="bar mt-3" style={{ marginTop: 4 }}>
              <div className="progress-fill" style={{ width: `${pct}%`, background: ZONE_COLOURS[z] || '#0061A2' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Failure list ────────────────────────────────────────────────
function FailureList({ rows }: { rows: Row[] }) {
  const failures = rows.filter((r) => r.st === 'Failed').sort((a, b) => (b.ft || '').localeCompare(a.ft || ''));
  return (
    <section className="card card-pad rise">
      <div className="d-between" style={{ marginBottom: 12 }}>
        <div>
          <div className="sectiontitle">Failure Reasons</div>
          <p className="section-sub">From driver attempt notes</p>
        </div>
        <span className="pill" style={{ background: '#FFEBE9', color: '#FF5245' }}>
          <span className="dot" style={{ background: '#FF5245' }} />
          {failures.length}
        </span>
      </div>
      <div className="scroll-y" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {failures.length === 0 ? (
          <div className="empty">No failed deliveries for the selected period</div>
        ) : (
          failures.map((f) => (
            <div className="fail-item" key={f.id}>
              <div className="d-between d-wrap" style={{ marginBottom: 6, gap: 8 }}>
                <div style={{ fontWeight: 600, color: '#003D6C' }}>{f.so || f.ref}</div>
                {f.r && (
                  <span className="pill" style={{ background: (ZONE_COLOURS[f.r] || '#EAF2FB') + '20', color: ZONE_COLOURS[f.r] || '#384553' }}>
                    {f.r}
                  </span>
                )}
              </div>
              <div className="muted" style={{ marginBottom: 6 }}>
                {f.dr} · {formatDateAU(f.d)}
              </div>
              <div className="italic" style={{ color: '#384553' }}>
                &ldquo;{(f.fn || 'No reason recorded').slice(0, 180)}&rdquo;
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ── Sales order search ──────────────────────────────────────────
function OrderSearch({ data }: { data: Row[] }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matches =
    q.length < 3
      ? []
      : data.filter((r) => (r.so && r.so.toLowerCase().includes(q)) || (r.ref && r.ref.toLowerCase().includes(q)));

  return (
    <section className="card card-pad mb-5 rise">
      <div className="d-between d-wrap" style={{ marginBottom: 12, gap: 12 }}>
        <div>
          <div className="sectiontitle">Sales Order Lookup</div>
          <p className="section-sub">Search by sales order reference or stop reference</p>
        </div>
        <div className="d-center d-gap-2 d-grow" style={{ maxWidth: 420 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <svg
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#737474"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input className="input-base" style={{ paddingLeft: 34 }} placeholder="Order or stop ref" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button className="btn btn-ghost" onClick={() => setQuery('')}>
            Clear
          </button>
        </div>
      </div>
      <div>
        {q.length > 0 && q.length < 3 && <div className="muted" style={{ fontSize: 12, padding: '8px 0' }}>Enter at least 3 characters to search</div>}
        {q.length >= 3 && matches.length === 0 && <div className="muted" style={{ fontSize: 12, padding: '8px 0' }}>No matches found</div>}
        {matches.slice(0, 5).map((r) => (
          <SearchResult key={r.id} r={r} />
        ))}
        {matches.length > 5 && <div className="muted" style={{ textAlign: 'center', fontSize: 12, padding: 8 }}>+ {matches.length - 5} more matches</div>}
      </div>
    </section>
  );
}

function SearchResult({ r }: { r: Row }) {
  const color = STATUS_COLOURS[r.st] || '#737474';
  return (
    <div className="search-result">
      <div className="d-between d-wrap" style={{ gap: 8, marginBottom: 12 }}>
        <div>
          <div className="kv-label">Sales order · Stop reference</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#003D6C' }}>
            {r.so || '—'} <span style={{ fontSize: 12, fontWeight: 400, color: '#737474' }}>· {r.ref}</span>
          </div>
        </div>
        <div className="d-center d-gap-2">
          <span className="pill" style={{ background: color + '20', color }}>
            <span className="dot" style={{ background: color }} />
            {r.st}
          </span>
          {r.r && (
            <span className="pill" style={{ background: (ZONE_COLOURS[r.r] || '#384553') + '20', color: ZONE_COLOURS[r.r] || '#384553' }}>
              {r.r}
            </span>
          )}
        </div>
      </div>
      <div className="search-grid">
        <div>
          <div className="kv-label">Delivery Address</div>
          <div style={{ color: '#384553' }}>{r.a || '—'}</div>
        </div>
        <div>
          <div className="kv-label">Driver</div>
          <div style={{ color: '#384553', fontWeight: 500 }}>{r.dr || '—'}</div>
          <div className="muted" style={{ marginTop: 2 }}>Trip date: {formatDateAU(r.d)}</div>
        </div>
        <div>
          <div className="kv-label">Timeline</div>
          <div className="num-mono" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {r.et && <Row2 label="Enroute" value={formatTimeAU(r.et)} />}
            {r.at && <Row2 label="Arrived" value={formatTimeAU(r.at)} />}
            {r.ct && <Row2 label="Completed" value={formatTimeAU(r.ct)} color="#0BDA8E" />}
            {r.ft && <Row2 label="Failed" value={formatTimeAU(r.ft)} color="#FF5245" />}
            <div className="d-between" style={{ paddingTop: 4, marginTop: 4, borderTop: '1px solid #E3E3E3' }}>
              <span className="muted">DIFOT</span>
              {r.st === 'Complete' ? (
                <span style={{ color: r.ot ? '#0BDA8E' : '#FF9500', fontWeight: 600 }}>{r.ot ? 'On time' : 'Late'}</span>
              ) : (
                <span className="muted">—</span>
              )}
            </div>
          </div>
        </div>
      </div>
      {r.fn && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E3E3E3', fontSize: 12 }}>
          <span className="kv-label" style={{ display: 'inline' }}>
            Failure note ·{' '}
          </span>
          <span className="italic" style={{ color: '#384553' }}>
            &ldquo;{r.fn}&rdquo;
          </span>
        </div>
      )}
      {r.sd != null && (
        <div className="muted" style={{ marginTop: 8, fontSize: 11 }}>
          Service time: <strong style={{ color: '#384553' }}>{r.sd.toFixed(1)} min</strong>
          {r.td != null && (
            <>
              {' '}
              · Travel: <strong style={{ color: '#384553' }}>{r.td.toFixed(1)} min</strong>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row2({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="d-between">
      <span className="muted">{label}</span>
      <span style={color ? { color, fontWeight: 600 } : undefined}>{value}</span>
    </div>
  );
}

// ── Driver leaderboard ──────────────────────────────────────────
type DriverSort = 'difot' | 'volume' | 'failed' | 'distance' | 'name';

function DriverLeaderboard({ rows, onPickDriver }: { rows: Row[]; onPickDriver: (name: string) => void }) {
  const [sortBy, setSortBy] = useState<DriverSort>('volume');

  const list = useMemo(() => {
    const byDriver: Record<
      string,
      {
        name: string;
        team: string | null;
        total: number;
        complete: number;
        failed: number;
        pending: number;
        difot: number;
        denom: number;
        zones: Set<string>;
        services: number[];
        lastDate: string | null;
        distancesByDay: Record<string, number>;
      }
    > = {};
    rows.forEach((r) => {
      if (!r.dr) return;
      if (!byDriver[r.dr]) {
        byDriver[r.dr] = {
          name: r.dr,
          team: r.tm,
          total: 0,
          complete: 0,
          failed: 0,
          pending: 0,
          difot: 0,
          denom: 0,
          zones: new Set(),
          services: [],
          lastDate: null,
          distancesByDay: {},
        };
      }
      const d = byDriver[r.dr];
      d.total++;
      if (r.st !== 'Cancelled') d.denom++;
      if (r.st === 'Complete') d.complete++;
      if (r.st === 'Failed') d.failed++;
      if (r.st === 'Pending' || r.st === 'Enroute' || r.st === 'Arrived') d.pending++;
      if (r.st === 'Complete' && r.ot === true) d.difot++;
      if (r.r) d.zones.add(r.r);
      if (r.sd != null && r.sd >= 0 && r.sd < 240) d.services.push(r.sd);
      if (r.ct && (!d.lastDate || r.ct > d.lastDate)) d.lastDate = r.ct;
      if (r.dk != null) {
        const cur = d.distancesByDay[r.d] || 0;
        if (r.dk > cur) d.distancesByDay[r.d] = r.dk;
      }
    });
    const arr = Object.values(byDriver).map((d) => ({
      ...d,
      difotPct: d.denom ? (d.difot / d.denom) * 100 : 0,
      avgService: d.services.length ? d.services.reduce((a, b) => a + b, 0) / d.services.length : null,
      totalKm: Object.values(d.distancesByDay).reduce((a, b) => a + b, 0),
      activeDays: Object.keys(d.distancesByDay).length,
    }));
    if (sortBy === 'difot') arr.sort((a, b) => b.difotPct - a.difotPct);
    else if (sortBy === 'volume') arr.sort((a, b) => b.total - a.total);
    else if (sortBy === 'failed') arr.sort((a, b) => b.failed - a.failed);
    else if (sortBy === 'distance') arr.sort((a, b) => b.totalKm - a.totalKm);
    else if (sortBy === 'name') arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [rows, sortBy]);

  return (
    <section className="card mb-5 rise" style={{ overflow: 'hidden' }}>
      <div className="d-between d-wrap card-pad" style={{ paddingBottom: 12, gap: 8 }}>
        <div>
          <div className="sectiontitle">Driver Performance Leaderboard</div>
          <p className="section-sub">Click a driver name to filter the dashboard to their stops</p>
        </div>
        <div className="d-center d-gap-2" style={{ fontSize: 11, color: '#737474' }}>
          <span>Sort by</span>
          <select className="input-base input-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value as DriverSort)}>
            <option value="difot">DIFOT %</option>
            <option value="volume">Total stops</option>
            <option value="failed">Failed count</option>
            <option value="distance">Distance travelled</option>
            <option value="name">Driver name</option>
          </select>
        </div>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th className="t-left">Driver</th>
              <th className="t-left">Team</th>
              <th className="t-right">Stops</th>
              <th className="t-right">Delivered</th>
              <th className="t-right">Failed</th>
              <th className="t-right">Pending</th>
              <th className="t-left" style={{ width: 200 }}>
                DIFOT
              </th>
              <th className="t-right">Avg service</th>
              <th className="t-right">Distance</th>
              <th className="t-right">Zones</th>
              <th className="t-right">Last delivery</th>
            </tr>
          </thead>
          <tbody>
            {list.map((d) => {
              const tone = difotTone(d.difotPct);
              return (
                <tr key={d.name}>
                  <td className="t-left">
                    <button className="link-btn" onClick={() => onPickDriver(d.name)}>
                      {d.name}
                    </button>
                  </td>
                  <td className="t-left">
                    <span className="pill" style={{ background: '#E6F0F8', color: '#0061A2' }}>
                      {d.team || '—'}
                    </span>
                  </td>
                  <td className="t-right num-mono" style={{ fontWeight: 500 }}>
                    {d.total}
                  </td>
                  <td className="t-right num-mono">
                    <span style={{ color: '#0BDA8E' }}>{d.complete}</span>
                  </td>
                  <td className="t-right num-mono">
                    {d.failed > 0 ? <span style={{ color: '#FF5245', fontWeight: 600 }}>{d.failed}</span> : <span className="muted">0</span>}
                  </td>
                  <td className="t-right num-mono">
                    <span style={{ color: '#B37D6C' }}>{d.pending}</span>
                  </td>
                  <td>
                    <div className="d-center d-gap-2">
                      <div className="bar d-grow">
                        <div className="progress-fill" style={{ width: `${d.difotPct}%`, background: tone }} />
                      </div>
                      <span className="num-mono" style={{ fontWeight: 600, fontSize: 12, width: 48, textAlign: 'right', color: tone }}>
                        {d.difotPct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="t-right num-mono">{d.avgService != null ? d.avgService.toFixed(1) + 'm' : '—'}</td>
                  <td className="t-right">
                    {d.totalKm > 0 ? (
                      <>
                        <span className="num-mono" style={{ fontWeight: 500, color: '#003D6C' }}>
                          {d.totalKm.toFixed(0)}
                        </span>
                        <span style={{ fontSize: 10, color: '#737474', marginLeft: 4 }}>km{d.activeDays > 1 ? ` / ${d.activeDays}d` : ''}</span>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="t-right muted">
                    {d.zones.size > 0 ? [...d.zones].slice(0, 3).join(', ') + (d.zones.size > 3 ? ` +${d.zones.size - 3}` : '') : '—'}
                  </td>
                  <td className="t-right muted">{d.lastDate ? formatTimeAU(d.lastDate) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Failed reasons deep dive ────────────────────────────────────
function FailedDeepDive({ data, filtered }: { data: Row[]; filtered: Row[] }) {
  const [ddRange, setDdRange] = useState<'inherit' | DateRange>('inherit');
  const [team, setTeam] = useState('');
  const [driver, setDriver] = useState('');
  const [category, setCategory] = useState('');

  const baseFailed = useMemo(() => {
    let rows: Row[];
    if (ddRange === 'inherit') rows = filtered;
    else rows = narrowByRange(data, ddRange);
    return rows.filter((r) => r.st === 'Failed').map((r) => ({ ...r, _cat: categoriseFailure(r.fn) }));
  }, [data, filtered, ddRange]);

  const rows = useMemo(() => {
    let r = baseFailed;
    if (team) r = r.filter((x) => x.tm === team);
    if (driver) r = r.filter((x) => x.dr === driver);
    if (category) r = r.filter((x) => x._cat === category);
    return r;
  }, [baseFailed, team, driver, category]);

  const headlineCount = ddRange === 'inherit' ? filtered.filter((r) => r.st === 'Failed').length : baseFailed.length;

  const teams = useMemo(() => {
    const c: Record<string, number> = {};
    data.filter((r) => r.st === 'Failed' && r.tm).forEach((r) => (c[r.tm as string] = (c[r.tm as string] || 0) + 1));
    return Object.keys(c).sort((a, b) => c[b] - c[a]).map((t) => ({ t, n: c[t] }));
  }, [data]);
  const driverOpts = useMemo(() => {
    const c: Record<string, number> = {};
    data.filter((r) => r.st === 'Failed' && r.dr).forEach((r) => (c[r.dr as string] = (c[r.dr as string] || 0) + 1));
    return Object.keys(c).sort((a, b) => c[b] - c[a]).map((d) => ({ d, n: c[d] }));
  }, [data]);

  const catCounts: Record<string, number> = {};
  rows.forEach((r) => (catCounts[r._cat] = (catCounts[r._cat] || 0) + 1));
  const drvCounts: Record<string, number> = {};
  rows.forEach((r) => {
    if (r.dr) drvCounts[r.dr] = (drvCounts[r.dr] || 0) + 1;
  });
  const total = rows.length;
  const uniqueDrivers = new Set(rows.map((r) => r.dr).filter(Boolean)).size;
  const uniqueTeams = new Set(rows.map((r) => r.tm).filter(Boolean)).size;
  const topCatKey = Object.keys(catCounts).sort((a, b) => catCounts[b] - catCounts[a])[0];
  const topCat = FAIL_CATEGORIES.find((c) => c.key === topCatKey);
  const worstDriver = Object.keys(drvCounts).sort((a, b) => drvCounts[b] - drvCounts[a])[0];

  const kpis = [
    { label: 'Total failures', value: total.toLocaleString(), sub: uniqueTeams ? `${uniqueTeams} team${uniqueTeams !== 1 ? 's' : ''} affected` : 'none', color: '#FF5245', text: false },
    { label: 'Drivers affected', value: String(uniqueDrivers), sub: total ? `${(total / Math.max(1, uniqueDrivers)).toFixed(1)} avg/driver` : 'none', color: '#0061A2', text: false },
    { label: 'Top category', value: topCat ? topCat.label : '—', sub: topCat ? `${catCounts[topCatKey]} failures (${total ? ((catCounts[topCatKey] / total) * 100).toFixed(0) : 0}%)` : '', color: topCat ? topCat.color : '#737474', text: true },
    { label: 'Most failures', value: worstDriver || '—', sub: worstDriver ? `${drvCounts[worstDriver]} failed stop${drvCounts[worstDriver] !== 1 ? 's' : ''}` : '', color: '#FF9500', text: true },
  ];

  const orderedCats = FAIL_CATEGORIES.map((c) => ({ ...c, count: catCounts[c.key] || 0 })).sort((a, b) => b.count - a.count);
  const peakCat = orderedCats[0]?.count || 1;

  const driverFails = Object.values(
    rows.reduce<Record<string, { name: string; team: string | null; count: number; cats: Record<string, number> }>>((acc, r) => {
      if (!r.dr) return acc;
      if (!acc[r.dr]) acc[r.dr] = { name: r.dr, team: r.tm, count: 0, cats: {} };
      acc[r.dr].count++;
      acc[r.dr].cats[r._cat] = (acc[r.dr].cats[r._cat] || 0) + 1;
      return acc;
    }, {}),
  ).sort((a, b) => b.count - a.count);
  const peakDrv = driverFails[0]?.count || 1;

  const detail = [...rows].sort((a, b) => (b.ft || '').localeCompare(a.ft || ''));

  return (
    <details className="card mb-5 rise">
      <summary>
        <div className="d-center d-gap-3">
          <span style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFE9E6', color: '#FF5245' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </span>
          <div>
            <div className="sectiontitle">Failed Reasons Deep Dive</div>
            <p className="section-sub">
              Click to expand · <span style={{ fontWeight: 600, color: '#FF5245' }}>{headlineCount}</span> failures across the selected window
            </p>
          </div>
        </div>
        <svg className="chevron muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </summary>

      <div className="dd-body">
        <div className="card card-pad mb-5" style={{ padding: 12, background: '#fff' }}>
          <div className="dd-filter-grid">
            <div>
              <label className="field-label">Date Range</label>
              <select className="input-base input-sm" value={ddRange} onChange={(e) => setDdRange(e.target.value as 'inherit' | DateRange)}>
                <option value="inherit">Use dashboard filter</option>
                <option value="all">All dates</option>
                <option value="today">Today only</option>
                <option value="yesterday">Yesterday only</option>
                <option value="last7">Last 7 days</option>
                <option value="last30">Last 30 days</option>
                <option value="thisMonth">This month</option>
              </select>
            </div>
            <div>
              <label className="field-label">Team / Vehicle</label>
              <select className="input-base input-sm" value={team} onChange={(e) => setTeam(e.target.value)}>
                <option value="">All teams</option>
                {teams.map(({ t, n }) => (
                  <option key={t} value={t}>
                    {t} ({n} failed)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Driver</label>
              <select className="input-base input-sm" value={driver} onChange={(e) => setDriver(e.target.value)}>
                <option value="">All drivers</option>
                {driverOpts.map(({ d, n }) => (
                  <option key={d} value={d}>
                    {d} ({n})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Category</label>
              <select className="input-base input-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">All categories</option>
                {FAIL_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="dd-kpis mb-5">
          {kpis.map((k) => (
            <div className="kpi-card" key={k.label} style={{ padding: '14px 16px' }}>
              <div className="accent" style={{ background: k.color }} />
              <div className="kpi-label">{k.label}</div>
              <div className={`kpi-value${k.text ? ' text' : ' num-mono'}`} style={{ color: k.color, fontSize: k.text ? 16 : 20, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.value}>
                {k.value}
              </div>
              <div className="kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>

        <div className="two-col mb-5">
          <div className="card card-pad" style={{ background: '#fff' }}>
            <div className="sectiontitle" style={{ marginBottom: 12 }}>
              Failures by Category
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {orderedCats.map((c) => {
                const pct = total ? (c.count / total) * 100 : 0;
                const barPct = peakCat ? (c.count / peakCat) * 100 : 0;
                return (
                  <div className="cat-row" key={c.key}>
                    <div className="cat-label">
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.color }} />
                      {c.label}
                    </div>
                    <div className="bar d-grow" style={{ height: 8 }}>
                      <div className="progress-fill" style={{ width: `${barPct}%`, background: c.color }} />
                    </div>
                    <span className="num-mono" style={{ fontWeight: 600, fontSize: 12, width: 32, textAlign: 'right', color: '#003D6C' }}>
                      {c.count}
                    </span>
                    <span className="num-mono muted" style={{ fontSize: 10, width: 36, textAlign: 'right' }}>
                      {pct.toFixed(0)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card card-pad" style={{ background: '#fff' }}>
            <div className="sectiontitle" style={{ marginBottom: 12 }}>
              Failures by Driver
            </div>
            <div className="scroll-y" style={{ maxHeight: 260, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {driverFails.length === 0 ? (
                <div className="empty">No failures matching these filters</div>
              ) : (
                driverFails.map((d) => {
                  const barPct = peakDrv ? (d.count / peakDrv) * 100 : 0;
                  const topKey = Object.keys(d.cats).sort((a, b) => d.cats[b] - d.cats[a])[0];
                  const topLabel = FAIL_CATEGORIES.find((c) => c.key === topKey)?.label || '';
                  return (
                    <div className="d-center d-gap-2" key={d.name} style={{ padding: '4px 0' }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#384553', width: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }} title={d.name}>
                        {d.name}
                      </span>
                      <span className="pill" style={{ background: '#E6F0F8', color: '#0061A2', flexShrink: 0, fontSize: 9 }}>
                        {d.team || '—'}
                      </span>
                      <div className="bar d-grow" style={{ height: 8 }}>
                        <div className="progress-fill" style={{ width: `${barPct}%`, background: '#FF5245' }} />
                      </div>
                      <span className="num-mono" style={{ fontWeight: 600, fontSize: 12, width: 24, textAlign: 'right', color: '#FF5245' }}>
                        {d.count}
                      </span>
                      <span className="muted" style={{ fontSize: 10, width: 110, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {topLabel}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="card card-pad" style={{ background: '#fff' }}>
          <div className="d-between" style={{ marginBottom: 12 }}>
            <div className="sectiontitle">Failure Detail</div>
            <span className="muted" style={{ fontSize: 10 }}>{detail.length} shown</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {detail.length === 0 ? (
              <div className="empty">No failures to show</div>
            ) : (
              detail.slice(0, 30).map((r) => {
                const cat = FAIL_CATEGORIES.find((c) => c.key === r._cat);
                return (
                  <div key={r.id} style={{ border: '1px solid #E3E3E3', borderRadius: 6, padding: 12, background: '#fff' }}>
                    <div className="d-between d-wrap" style={{ gap: 12, marginBottom: 6, alignItems: 'flex-start' }}>
                      <div className="d-center d-wrap d-gap-2">
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#003D6C' }}>{r.so || r.ref}</span>
                        <span className="pill" style={{ background: (cat?.color || '#737474') + '1A', color: cat?.color }}>
                          {cat?.label}
                        </span>
                        {r.r && (
                          <span className="pill" style={{ background: (ZONE_COLOURS[r.r] || '#EAF2FB') + '20', color: ZONE_COLOURS[r.r] || '#384553' }}>
                            {r.r}
                          </span>
                        )}
                        <span className="pill" style={{ background: '#E6F0F8', color: '#0061A2' }}>
                          {r.tm || '—'}
                        </span>
                      </div>
                      <span className="num-mono muted" style={{ fontSize: 10 }}>
                        {formatDateAU(r.d)} · {r.ft ? formatTimeAU(r.ft) : '—'}
                      </span>
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
                      {r.dr || ''} · {r.a ? r.a.replace(/, *Australia.*$/, '').slice(0, 100) : ''}
                    </div>
                    {r.fn ? (
                      <div className="italic" style={{ fontSize: 12, color: '#384553', marginTop: 6, paddingLeft: 12, borderLeft: '2px solid rgba(255,82,69,0.4)' }}>
                        &ldquo;{r.fn}&rdquo;
                      </div>
                    ) : (
                      <div className="muted italic" style={{ fontSize: 11, marginTop: 6 }}>
                        No reason note recorded
                      </div>
                    )}
                  </div>
                );
              })
            )}
            {detail.length > 30 && <div className="muted" style={{ textAlign: 'center', fontSize: 12, padding: 8 }}>+ {detail.length - 30} more failures · narrow filters to see all</div>}
          </div>
        </div>
      </div>
    </details>
  );
}

// ── Today operations ────────────────────────────────────────────
function TodayOps({ data, filtered }: { data: Row[]; filtered: Row[] }) {
  const today = latestDate(data);
  const todayRows = today ? filtered.filter((r) => r.d === today) : [];
  const order: Record<string, number> = { Failed: 0, Pending: 1, Enroute: 1, Arrived: 1, Complete: 2, Cancelled: 3 };
  const sorted = [...todayRows].sort((a, b) => {
    const oa = order[a.st] ?? 2;
    const ob = order[b.st] ?? 2;
    if (oa !== ob) return oa - ob;
    return (b.ct || b.ft || b.at || b.et || '').localeCompare(a.ct || a.ft || a.at || a.et || '');
  });

  return (
    <section className="card card-pad mb-5 rise">
      <div className="d-between d-wrap" style={{ marginBottom: 12, gap: 8 }}>
        <div>
          <div className="sectiontitle">Latest Day Operations</div>
          <p className="section-sub">
            Snapshot of stops scheduled for <span className="strong" style={{ color: '#384553', fontWeight: 500 }}>{formatDateAU(today)}</span>
          </p>
        </div>
        <div className="d-center d-wrap d-gap-2" style={{ fontSize: 11 }}>
          <span className="pill" style={{ background: '#E8F8F1', color: '#089464' }}>
            <span className="dot" style={{ background: '#0BDA8E' }} />
            Complete <span className="num-mono">{todayRows.filter((r) => r.st === 'Complete').length}</span>
          </span>
          <span className="pill" style={{ background: '#FFE9E6', color: '#D9382B' }}>
            <span className="dot" style={{ background: '#FF5245' }} />
            Failed <span className="num-mono">{todayRows.filter((r) => r.st === 'Failed').length}</span>
          </span>
          <span className="pill" style={{ background: '#FBEEEA', color: '#8C5E51' }}>
            <span className="dot" style={{ background: '#B37D6C' }} />
            Pending <span className="num-mono">{todayRows.filter((r) => ['Pending', 'Enroute', 'Arrived'].includes(r.st)).length}</span>
          </span>
        </div>
      </div>
      <div className="scroll-y" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sorted.length === 0 ? (
          <div className="empty">No stops scheduled</div>
        ) : (
          <>
            {sorted.slice(0, 60).map((r) => {
              const color = STATUS_COLOURS[r.st] || '#737474';
              const time = r.ct || r.ft || r.at || r.et;
              return (
                <div className="stop-row" key={r.id}>
                  <span className="dot" style={{ background: color, width: 9, height: 9, flexShrink: 0 }} />
                  <span className="stop-status" style={{ color }}>
                    {r.st}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#003D6C', width: 120, flexShrink: 0 }}>{r.so || r.ref || '—'}</span>
                  <span className="d-grow" style={{ fontSize: 12, color: '#384553', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.a ? r.a.replace(/, *Australia.*$/, '').slice(0, 90) : ''}
                  </span>
                  <span className="muted" style={{ fontSize: 10, width: 140, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.dr || ''}
                  </span>
                  <span className="num-mono muted d-center" style={{ fontSize: 10, width: 64, justifyContent: 'flex-end', gap: 4 }}>
                    {r.st === 'Complete' && r.ot === true && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0BDA8E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {time ? formatTimeAU(time) : '—'}
                  </span>
                </div>
              );
            })}
            {sorted.length > 60 && <div className="muted" style={{ textAlign: 'center', fontSize: 12, padding: 8 }}>+ {sorted.length - 60} more stops</div>}
          </>
        )}
      </div>
    </section>
  );
}
